import { momentizePeriod, toGQLPeriod } from "@ct-react/core";
import { AccommodationType, AgeCategory } from "@shared/models/article";
import {
  AnnualSearchInput,
  AnySearchInput,
  RentalSearchInput,
  SaleSearchInput,
  searchAnyRental,
  SearchPagination,
  searchWithPeriod,
  searchWithPrice,
  SeasonalSearchInput
} from "../models/search";

export const parseQuerySearchParams = (params: URLSearchParams, type: AccommodationType): AnySearchInput => {

  const keys = Array.from(Object.keys(params));
  const objectTypes = params.has("objectTypes") ? params.get("objectTypes")!.split("-") : params.getAll("objectType");
  const surface = params.has("minSurface") ? params.get("minSurface") : params.get("surface");
  const oldRooms = params.has("roomsRange") ? params.get("roomsRange")!.split("-") : undefined;
  const oldPrice = params.has("price") ? params.get("price")!.split("-") : undefined;

  const common = {
    ...(objectTypes.length > 0) && { objectTypes: objectTypes.map(val => +val) },
    ...(params.has("name")) && { name: params.get("name")! },
    ...(!!surface && +surface > 0) && { surface: +surface },
    ...(oldRooms && oldRooms.length === 2) && { roomsRange: [ oldRooms[0], oldRooms[1] ] },
    ...(params.has("rooms_min") && params.has("rooms_max")) && {
      roomsRange: [ +params.get("rooms_min")!, +params.get("rooms_max")! ]
    },
    ...(params.has("parking")) && { parking: true },
    ...(params.has("pool")) && { pool: true }
  }

  const price = {
    ...(!!oldPrice && oldPrice.length === 2) && { priceRange: oldPrice.map(val => +val) },
    ...(params.has("price_max")) && {
      priceRange: [ params.has("price_min") ? +params.get("price_min")! : 0, +params.get("price_max")! ]
    }
  }

  switch (type) {

    case AccommodationType.RENTAL:
      const oldGuests = params.get("minGuests");
      return {
        ...common,
        ...(params.has("start") && params.has("end")) && {
          period: {
            period: momentizePeriod([ params.get("start")!, params.get("end")! ])
          }
        },
        ...(keys.some(pk => /^period_/.test(pk))) && {
          period: {
            period: momentizePeriod([ params.get("period_start")!, params.get("period_end")! ])
          }
        },
        ...(!!oldGuests) && { guests: { [AgeCategory.ADULT]: +oldGuests, [AgeCategory.CHILD]: 0, [AgeCategory.EXEMPT]: 0 } },
        ...(keys.some(pk => /^guests_/.test(pk))) && {
          guests: {
            [AgeCategory.ADULT]: params.has("guests_adult") ? +params.get("guests_adult")! : 0,
            [AgeCategory.CHILD]: params.has("guests_child") ? +params.get("guests_child")! : 0,
            [AgeCategory.EXEMPT]: params.has("guests_exempt") ? +params.get("guests_exempt")! : 0
          }
        },
        ...(params.has("wifi")) && { wifi: true },
        ...(params.has("pet")) && { pet: true },
        ...(params.has("cityCenter") || params.has("city_center")) && { cityCenter: true },
        ...(params.has("skiArea") || params.has("ski_area")) && { skiArea: true }
      } as RentalSearchInput;

    case AccommodationType.SEASONAL:
      return {
        ...common,
        ...(Object.keys(price).length > 0) && { ...price },
        ...(params.has("wifi")) && { wifi: true },
        ...(params.has("pet")) && { pet: true },
        ...(params.has("cityCenter") || params.has("city_center")) && { cityCenter: true },
        ...(params.has("skiArea") || params.has("ski_area")) && { skiArea: true }
      } as SeasonalSearchInput;

    case AccommodationType.ANNUAL:
      return {
        ...common,
        ...(Object.keys(price).length > 0) && { ...price }
      } as AnnualSearchInput;

    case AccommodationType.SALE:
      return {
        ...common,
        ...(Object.keys(price).length > 0) && { ...price }
      } as SaleSearchInput;

    default:
      return {};
  }

}

export const rebuildQuerySearchParams = (params: URLSearchParams, value: AnySearchInput) => {

  const keys = Array.from(params.keys());

  params.has("objectTypes") && params.delete("objectTypes");
  params.has("objectType") && params.delete("objectType");
  !!value.objectTypes && value.objectTypes.forEach(val => params.append("objectType", val.toString()));

  params.has("name") && params.delete("name");
  !!value.name && params.set("name", value.name);

  params.has("minSurface") && params.delete("minSurface");
  params.has("surface") && params.delete("surface");
  !!value.surface && params.set("surface", value.surface.toString());

  keys.filter(pk => pk.match(/^rooms_/)).forEach(pk => params.delete(pk));
  !!value.roomsRange && params.set("rooms_min", value.roomsRange[0].toString());
  !!value.roomsRange && params.set("rooms_max", value.roomsRange[1].toString());

  (params.has("parking") && !value.parking) && params.delete("parking");
  value.parking && params.set("parking", true.toString());

  (params.has("pool") && !value.pool) && params.delete("pool");
  value.pool && params.set("pool", true.toString());

  keys.filter(pk => pk.match(/^guests_/)).forEach(pk => params.delete(pk));
  params.has("minGuests") && params.delete("minGuests");
  if (searchAnyRental(value)) {

    if (!!value.guests) {
      value.guests.ADULT > 0 && params.set("guests_adult", value.guests.ADULT.toString());
      value.guests.CHILD > 0 && params.set("guests_child", value.guests.CHILD.toString());
      value.guests.EXEMPT > 0 && params.set("guests_exempt", value.guests.EXEMPT.toString());
    }

    params.has("wifi") && params.delete("wifi");
    !!value.wifi && params.set("wifi", value.wifi.toString());

    params.has("pet") && params.delete("pet");
    !!value.pet && params.set("pet", value.pet.toString());

    params.has("cityCenter") && params.delete("cityCenter");
    params.has("city_center") && params.delete("city_center");
    !!value.cityCenter && params.set("city_center", value.cityCenter.toString());

    params.has("skiArea") && params.delete("skiArea");
    params.has("ski_area") && params.delete("ski_area");
    !!value.skiArea && params.set("ski_area", value.skiArea.toString());

  }

  keys.filter(pk => pk.match(/^price_/)).forEach(pk => params.delete(pk));
  if (searchWithPrice(value) && !!value.priceRange) {
    value.priceRange[0] > 0 && params.set("price_min", value.priceRange[0].toString());
    params.set("price_max", value.priceRange[1].toString());
  }

  params.has("start") && params.delete("start");
  params.has("end") && params.delete("end");
  keys.filter(pk => pk.match(/^period_/)).forEach(pk => params.delete(pk));
  if (searchWithPeriod(value) && !!value.period) {
    const formatted = toGQLPeriod(value.period!.period);
    params.set("period_start", formatted[0]);
    params.set("period_end", formatted[1]);
  }
}

export const parsePaginationSearchParams = (params: URLSearchParams): SearchPagination => {
  const direction = params.get("dir");
  const cursor = params.get("cursor");
  return {
    direction: direction === "before" ? "before" : "after",
    ...(!!cursor) && { cursor }
  };
}

export const rebuildPaginationSearchParams = (params: URLSearchParams, value: SearchPagination) => {
  params.has("dir") && params.delete("dir");
  params.has("cursor") && params.delete("cursor");
  (value.direction !== "after" || !!value.cursor) && params.set("dir", value.direction);
  !!value.cursor && params.set("cursor", value.cursor);
}
